import React, { useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { FiActivity, FiUsers, FiFolder, FiAlertTriangle, FiChevronRight, FiZap, FiClock } from "react-icons/fi";
import {
  WidgetGrid, Widget, Section, SectionHeader, Group, Row, Heatmap, Meter, Skeleton, AvatarStack, IconTile, tokens,
} from "../styles";
import { Avatar, Sparkline, SegmentedControl } from "./primitives";
import { IOS, moduleMeta, screenLabel, relativeTime, isOnline, MODULE_META, PERIOD_TEXT } from "../labels";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const ChartTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: tokens.solid(theme),
      border: `1px solid ${tokens.separator(theme)}`,
      borderRadius: 10,
      padding: "10px 12px",
      boxShadow: tokens.dk(theme) ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
      fontSize: 12,
      fontFamily: "'JetBrains Mono', monospace",
      color: tokens.label(theme),
      minWidth: 130,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 2 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
            {p.name}
          </span>
          <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function Overview({ summary, users, projects, loading, period, onOpen, onGoTab }) {
  const theme = useTheme();
  const [rankTab, setRankTab] = useState("screens");

  const chartData = useMemo(
    () => (summary?.byDay || []).map((d) => ({
      ...d,
      label: new Date(`${d.day}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", ""),
    })),
    [summary]
  );

  const online = users.filter((u) => isOnline(u.lastAt));
  const errorRate = summary?.total ? (summary.errors / summary.total) * 100 : 0;
  const heatMax = Math.max(1, ...(summary?.heatmap || []).flat());
  const peak = useMemo(() => {
    let best = null;
    (summary?.heatmap || []).forEach((row, d) => row.forEach((v, h) => {
      if (v > 0 && (!best || v > best.v)) best = { d, h, v };
    }));
    return best;
  }, [summary]);

  const rankRows = rankTab === "screens"
    ? (summary?.byScreen || []).map((s) => ({ key: s.key, label: screenLabel(s.key), count: s.count, meta: MODULE_META.navigation }))
    : (summary?.byModule || []).filter((m) => m.key !== "navigation").map((m) => ({ key: m.key, label: moduleMeta(m.key).label, count: m.count, meta: moduleMeta(m.key) }));
  const rankMax = Math.max(1, ...rankRows.map((r) => r.count));

  const value = (v) => (loading ? <Skeleton $w="60px" $h={30} /> : v);

  return (
    <>
      <Section $first>
        <WidgetGrid>
          <Widget $accent={IOS.blue} $delay={0}>
            <span className="w-glow" />
            <div className="w-head"><FiActivity /> Eventos</div>
            <div className="w-value">{value((summary?.total || 0).toLocaleString("pt-BR"))}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{PERIOD_TEXT[period]}</span>
              <Sparkline data={(summary?.byDay || []).map((d) => d.total)} color={IOS.blue} width={80} height={24} />
            </div>
          </Widget>

          <Widget $accent={IOS.green} $delay={50} $clickable onClick={() => onGoTab("users")}>
            <span className="w-glow" />
            <div className="w-head"><FiUsers /> Usuários ativos</div>
            <div className="w-value">{value(summary?.activeUsers || 0)}</div>
            <div className="w-foot">
              {online.length > 0 ? (
                <><span style={{ width: 7, height: 7, borderRadius: "50%", background: IOS.green }} /> {online.length} online agora</>
              ) : "ninguém online agora"}
            </div>
          </Widget>

          <Widget $accent={IOS.indigo} $delay={100} $clickable onClick={() => onGoTab("projects")}>
            <span className="w-glow" />
            <div className="w-head"><FiFolder /> Projetos ativos</div>
            <div className="w-value">{value(summary?.activeProjects || 0)}</div>
            <div className="w-foot">
              {projects[0] ? <>mais ativo: <strong style={{ color: tokens.label(theme), fontWeight: 600 }}>{projects[0].name.split(" ").slice(0, 2).join(" ")}</strong></> : "—"}
            </div>
          </Widget>

          <Widget $accent={errorRate > 5 ? IOS.red : IOS.mint} $delay={150} $clickable onClick={() => onGoTab("timeline", { status: "error" })}>
            <span className="w-glow" />
            <div className="w-head"><FiAlertTriangle /> Taxa de erro</div>
            <div className="w-value">{value(<>{errorRate.toFixed(1)}<span className="w-unit">%</span></>)}</div>
            <div className="w-foot">{summary?.errors || 0} {summary?.errors === 1 ? "falha" : "falhas"} registradas</div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={150}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div>
                <div className="w-title">Atividade</div>
                <div className="w-sub">Ações, telas e tarefas por dia</div>
              </div>
              <div className="w-eyebrow" style={{ display: "flex", gap: 14 }}>
                {[["Ações", IOS.green], ["Telas", IOS.blue], ["Tarefas", IOS.purple]].map(([l, c]) => (
                  <span key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />{l}
                  </span>
                ))}
              </div>
            </div>
            {loading ? <Skeleton $h={210} $r={14} /> : (
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart data={chartData} margin={{ top: 10, right: 4, left: -18, bottom: 0 }}>
                  <defs>
                    {[["a", IOS.green], ["p", IOS.blue], ["t", IOS.purple]].map(([k, c]) => (
                      <linearGradient key={k} id={`ov-${k}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.02} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: tokens.label2(theme) }} axisLine={false} tickLine={false} minTickGap={18} />
                  <YAxis tick={{ fontSize: 10, fill: tokens.label2(theme) }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }} />
                  <Area type="monotone" dataKey="page_view" name="Telas" stackId="1" stroke={IOS.blue} strokeWidth={2} fill="url(#ov-p)" />
                  <Area type="monotone" dataKey="action" name="Ações" stackId="1" stroke={IOS.green} strokeWidth={2} fill="url(#ov-a)" />
                  <Area type="monotone" dataKey="task" name="Tarefas" stackId="1" stroke={IOS.purple} strokeWidth={2} fill="url(#ov-t)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Widget>

          <Widget $delay={200}>
            <div className="w-head" style={{ color: IOS.green }}><FiZap /> Agora</div>
            {online.length > 0 ? (
              <>
                <div className="w-value">{online.length}<span className="w-unit">online</span></div>
                <AvatarStack style={{ marginTop: 14 }}>
                  {online.slice(0, 6).map((u) => <Avatar key={u.id} id={u.id} name={u.name} color={u.color} size={30} />)}
                  {online.length > 6 && <span className="more">+{online.length - 6}</span>}
                </AvatarStack>
              </>
            ) : (
              <div style={{ fontSize: "0.85em", color: tokens.label2(theme), marginBottom: 6 }}>Ninguém ativo nos últimos 15 min</div>
            )}
            <div className="w-eyebrow" style={{ marginTop: 16 }}>
              Vistos por último
            </div>
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
              {users.slice(0, 4).map((u) => (
                <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }} onClick={() => onOpen("user", u)}>
                  <Avatar id={u.id} name={u.name} color={u.color} size={26} online={isOnline(u.lastAt)} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: "0.82em", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
                  <div style={{ fontSize: "0.74em", color: tokens.label2(theme) }}>{relativeTime(u.lastAt)}</div>
                </div>
              ))}
              {users.length === 0 && !loading && <div style={{ fontSize: "0.8em", color: tokens.label2(theme) }}>—</div>}
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <Widget $span={2} $delay={250}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div>
                <div className="w-title">Horários de uso</div>
                <div className="w-sub">
                  {peak ? <>Pico: <strong style={{ color: tokens.label(theme) }}>{WEEKDAYS[peak.d]} às {String(peak.h).padStart(2, "0")}h</strong></> : "Sem dados no período"}
                </div>
              </div>
              <FiClock style={{ color: tokens.label3(theme) }} />
            </div>
            <Heatmap>
              {WEEKDAYS.map((wd, d) => (
                <React.Fragment key={wd}>
                  <div className="day">{wd}</div>
                  {Array.from({ length: 24 }, (_, h) => {
                    const v = summary?.heatmap?.[d]?.[h] || 0;
                    const intensity = v / heatMax;
                    return (
                      <div
                        key={h}
                        className="cell"
                        title={`${wd} ${String(h).padStart(2, "0")}h — ${v} eventos`}
                        style={v ? { background: IOS.green, opacity: 0.2 + intensity * 0.8 } : undefined}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
              <div />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="hour">{h % 6 === 0 ? `${h}h` : ""}</div>
              ))}
            </Heatmap>
          </Widget>

          <Widget $span={2} $delay={300}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
              <div className="w-title">Mais usados</div>
              <SegmentedControl
                small
                value={rankTab}
                onChange={setRankTab}
                options={[{ key: "screens", label: "Telas" }, { key: "modules", label: "Ações" }]}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {rankRows.slice(0, 6).map((r) => {
                const Icon = r.meta.icon;
                return (
                  <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                    <IconTile $size={26} $color={r.meta.color}><Icon /></IconTile>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.84em" }}>
                        <span style={{ fontWeight: 500 }}>{r.label}</span>
                        <span style={{ color: tokens.label2(theme) }}>{r.count}</span>
                      </div>
                      <Meter $color={r.meta.color}><div style={{ width: `${(r.count / rankMax) * 100}%` }} /></Meter>
                    </div>
                  </div>
                );
              })}
              {rankRows.length === 0 && <div style={{ fontSize: "0.85em", color: tokens.label2(theme) }}>Sem dados no período</div>}
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={200}>
        <WidgetGrid $cols={2}>
          {[
            { kind: "project", title: "Projetos em destaque", tab: "projects", list: projects },
            { kind: "user", title: "Usuários em destaque", tab: "users", list: [...users].sort((a, b) => b.total - a.total) },
          ].map((block) => (
            <div key={block.kind}>
              <SectionHeader>
                <h2>{block.title}</h2>
                <button className="link" onClick={() => onGoTab(block.tab)}>Ver todos <FiChevronRight /></button>
              </SectionHeader>
              <Group>
                {block.list.slice(0, 5).map((it) => (
                  <Row key={it.id} $clickable $inset="64px" onClick={() => onOpen(block.kind, it)}>
                    <Avatar id={it.id} name={it.name} color={it.color} size={36} square={block.kind === "project"} online={block.kind === "user" && isOnline(it.lastAt)} />
                    <div className="main">
                      <div className="title">{it.name}</div>
                      <div className="subtitle">
                        {block.kind === "project"
                          ? `${it.othersCount} ${it.othersCount === 1 ? "usuário" : "usuários"} · ${relativeTime(it.lastAt)}`
                          : `${it.othersCount} ${it.othersCount === 1 ? "projeto" : "projetos"} · ${relativeTime(it.lastAt)}`}
                      </div>
                    </div>
                    <div className="trailing">
                      <Sparkline data={it.spark} color={block.kind === "project" ? IOS.indigo : IOS.green} width={64} height={22} />
                      <span className="value" style={{ minWidth: 28, textAlign: "right" }}>{it.total}</span>
                      <FiChevronRight className="chevron" />
                    </div>
                  </Row>
                ))}
                {block.list.length === 0 && (
                  <Row><div className="main"><div className="subtitle">{loading ? "Carregando..." : "Sem atividade no período"}</div></div></Row>
                )}
              </Group>
            </div>
          ))}
        </WidgetGrid>
      </Section>
    </>
  );
}
